import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { apiRequest } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { getTenantScope } from '../utils/authHeaders';
import type {
  ConversationSession,
  EmmaContext as EmmaConversationContext,
  MessageThread,
  UserPreferences,
} from '../types/emma.types';

// ===================================================================
// EMMA PERSISTENT STATE
// ===================================================================

export interface WorkflowStatus {
  id: string;
  status: 'running' | 'completed' | 'failed' | 'waiting_input';
  currentAgent: string;
  progress: number;
  message: string;
  timestamp: Date;
}

export interface StatusUpdate {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  timestamp: Date;
  actionable?: boolean;
}

export interface EmmaState {
  isVisible: boolean;
  currentWorkflow: WorkflowStatus | null;
  statusUpdates: StatusUpdate[];
  emergencyHelpAvailable: boolean;
  isMinimized: boolean;
  persistentInterface: {
    viewState: 'compact' | 'expanded' | 'collapsed';
    layout: 'split' | 'sidebar' | 'bottom-sheet';
    preferredWidth: number;
    autoExpand: boolean;
    position: 'left' | 'right';
  };
}

export interface EmmaPageContext {
  campaignId: string | null;
  workflowStep: number | null;
  orgId: string | null;
  partitionId: string | null;
  userId: string | null;
  pathname: string;
  pageLabel?: string;
  helpTopics?: string[];
}

interface EmmaContextType {
  emmaState: EmmaState;
  context: EmmaConversationContext;
  sessionId: string | null;
  pageContext: EmmaPageContext;
  isLoading: boolean;
  error: string | null;
  updateWorkflow: (workflow: WorkflowStatus | null) => void;
  addStatusUpdate: (update: Omit<StatusUpdate, 'id' | 'timestamp'>) => void;
  toggleMinimize: () => void;
  setVisibility: (visible: boolean) => void;
  clearHistory: () => void;
  updatePersistentInterface: (updates: Partial<EmmaState['persistentInterface']>) => void;
  updateContext: (updates: Partial<EmmaConversationContext>) => void;
  preserveContext: () => void;
  restoreContext: (targetSessionId: string) => Promise<void>;
  clearContext: () => void;
  setPageContext: (updates: Partial<EmmaPageContext>) => void;
}

const EMMA_STORAGE_KEY = 'emma_continuity_state';
const EMMA_CONTEXT_STORAGE_KEY = 'emma_context';
const EMMA_SESSION_STORAGE_KEY = 'emma_session';
const EMMA_SESSION_ID_STORAGE_KEY = 'emma_current_session_id';

const defaultEmmaState: EmmaState = {
  isVisible: true,
  currentWorkflow: null,
  statusUpdates: [],
  emergencyHelpAvailable: true,
  isMinimized: false,
  persistentInterface: {
    viewState: 'expanded',
    layout: 'split',
    preferredWidth: 35,
    autoExpand: true,
    position: 'right',
  },
};

const defaultConversationContext: EmmaConversationContext = {
  strategicRecommendations: [],
  workflowState: {
    activeWorkflows: [],
    completedWorkflows: [],
    queuedWorkflows: [],
    workflowProgress: {},
  },
  userPreferences: {
    conversationStyle: 'strategic',
    notificationLevel: 'standard',
    preferredAgents: [],
    autoExecuteRecommendations: false,
  },
  activeAgents: [],
  sessionId: '',
};

const EmmaContext = createContext<EmmaContextType | undefined>(undefined);

const generateSessionId = (): string =>
  `emma_session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const parseStoredJson = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const hydrateSession = (session: ConversationSession): ConversationSession => ({
  ...session,
  startTime: new Date(session.startTime),
  lastActivity: new Date(session.lastActivity),
  threads: (session.threads || []).map((thread: MessageThread) => ({
    ...thread,
    createdAt: new Date(thread.createdAt),
    lastActivity: new Date(thread.lastActivity),
    messages: (thread.messages || []).map((message) => ({
      ...message,
      timestamp: new Date(message.timestamp),
    })),
  })),
});

const parseRouteContext = (pathname: string): { campaignId: string | null; workflowStep: number | null } => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { campaignId: null, workflowStep: null };

  if (parts[0] === 'campaign' && parts[1]) {
    const campaignId = parts[1];
    if (parts[2] === 'workflow' && parts[3] && /^\d+$/.test(parts[3])) {
      return { campaignId, workflowStep: Number(parts[3]) };
    }
    return { campaignId, workflowStep: null };
  }

  if (parts[0] === 'campaigns' && parts[1]) {
    return { campaignId: parts[1], workflowStep: null };
  }

  if (parts[0] === 'workflow') {
    if (parts[1] && /^\d+$/.test(parts[1])) {
      return { campaignId: null, workflowStep: Number(parts[1]) };
    }
    if (parts[1] && parts[2] && /^\d+$/.test(parts[2])) {
      return { campaignId: parts[1], workflowStep: Number(parts[2]) };
    }
    return { campaignId: null, workflowStep: null };
  }

  return { campaignId: null, workflowStep: null };
};

const getBasePageContext = (
  user: { id: string; organization_id: string; partition_id: string | null } | null,
): EmmaPageContext => {
  const scope = getTenantScope();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const route = parseRouteContext(pathname);

  return {
    campaignId: route.campaignId,
    workflowStep: route.workflowStep,
    orgId: user?.organization_id || scope.organizationId || null,
    partitionId: user?.partition_id || scope.activePartitionId || null,
    userId: user?.id || null,
    pathname,
  };
};

export const EmmaContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const initializedRef = useRef(false);
  const { organizationId, activePartitionId } = getTenantScope();

  const [emmaState, setEmmaState] = useState<EmmaState>(() => {
    const stored = parseStoredJson<EmmaState>(typeof window !== 'undefined' ? localStorage.getItem(EMMA_STORAGE_KEY) : null);
    if (!stored) return defaultEmmaState;

    const hydrated: EmmaState = {
      ...defaultEmmaState,
      ...stored,
      currentWorkflow: stored.currentWorkflow
        ? {
            ...stored.currentWorkflow,
            timestamp: new Date(stored.currentWorkflow.timestamp),
          }
        : null,
      statusUpdates: (stored.statusUpdates || []).map((status) => ({
        ...status,
        timestamp: new Date(status.timestamp),
      })),
      persistentInterface: {
        ...defaultEmmaState.persistentInterface,
        ...stored.persistentInterface,
        viewState:
          stored.persistentInterface?.viewState === 'compact'
            ? 'expanded'
            : stored.persistentInterface?.viewState || defaultEmmaState.persistentInterface.viewState,
      },
    };

    return hydrated;
  });

  const [sessionId, setSessionId] = useState<string>(() => {
    const storedSessionId =
      typeof window !== 'undefined'
        ? localStorage.getItem(EMMA_SESSION_ID_STORAGE_KEY) ||
          parseStoredJson<ConversationSession>(localStorage.getItem(EMMA_SESSION_STORAGE_KEY))?.id
        : null;
    return storedSessionId || generateSessionId();
  });

  const [context, setContext] = useState<EmmaConversationContext>(() => {
    const stored = parseStoredJson<EmmaConversationContext>(
      typeof window !== 'undefined' ? localStorage.getItem(EMMA_CONTEXT_STORAGE_KEY) : null,
    );
    if (!stored) return { ...defaultConversationContext };
    return {
      ...defaultConversationContext,
      ...stored,
      userPreferences: {
        ...defaultConversationContext.userPreferences,
        ...(stored.userPreferences as UserPreferences),
      },
    };
  });

  const [currentSession, setCurrentSession] = useState<ConversationSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageContext, setPageContextState] = useState<EmmaPageContext>(() =>
    getBasePageContext(
      user
        ? {
            id: user.id,
            organization_id: user.organization_id,
            partition_id: user.partition_id,
          }
        : null,
    ),
  );

  const persistContext = useCallback(
    (nextContext: EmmaConversationContext, nextSession: ConversationSession | null, nextSessionId: string) => {
      try {
        localStorage.setItem(EMMA_CONTEXT_STORAGE_KEY, JSON.stringify(nextContext));
        localStorage.setItem(EMMA_SESSION_ID_STORAGE_KEY, nextSessionId);
        if (nextSession) {
          localStorage.setItem(EMMA_SESSION_STORAGE_KEY, JSON.stringify(nextSession));
        }
      } catch (persistError) {
        console.warn('Failed to persist Emma context/session:', persistError);
      }
    },
    [],
  );

  useEffect(() => {
    try {
      localStorage.setItem(EMMA_STORAGE_KEY, JSON.stringify(emmaState));
    } catch (persistError) {
      console.warn('Failed to persist Emma state:', persistError);
    }
  }, [emmaState]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const storedSession = parseStoredJson<ConversationSession>(localStorage.getItem(EMMA_SESSION_STORAGE_KEY));
    if (!storedSession) return;

    const hydrated = hydrateSession(storedSession);
    const ageMs = Date.now() - hydrated.lastActivity.getTime();
    if (ageMs >= 24 * 60 * 60 * 1000) return;

    setCurrentSession(hydrated);
    setSessionId(hydrated.id);
    setContext((prev) => ({
      ...prev,
      ...(hydrated.context || {}),
      sessionId: hydrated.id,
    }));
  }, []);

  useEffect(() => {
    const base = getBasePageContext(
      user
        ? {
            id: user.id,
            organization_id: user.organization_id,
            partition_id: user.partition_id,
          }
        : null,
    );
    setPageContextState((prev) => ({ ...prev, ...base }));
  }, [user?.id, user?.organization_id, user?.partition_id, organizationId, activePartitionId]);

  useEffect(() => {
    const syncFromLocation = () => {
      const base = getBasePageContext(
        user
          ? {
              id: user.id,
              organization_id: user.organization_id,
              partition_id: user.partition_id,
            }
          : null,
      );
      setPageContextState((prev) => ({ ...prev, ...base }));
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    const emitNavigationEvent = () => window.dispatchEvent(new Event('emma:navigation'));

    window.history.pushState = ((...args: Parameters<History['pushState']>) => {
      originalPushState.apply(window.history, args);
      emitNavigationEvent();
    }) as History['pushState'];

    window.history.replaceState = ((...args: Parameters<History['replaceState']>) => {
      originalReplaceState.apply(window.history, args);
      emitNavigationEvent();
    }) as History['replaceState'];

    window.addEventListener('popstate', syncFromLocation);
    window.addEventListener('emma:navigation', syncFromLocation);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', syncFromLocation);
      window.removeEventListener('emma:navigation', syncFromLocation);
    };
  }, [user?.id, user?.organization_id, user?.partition_id]);

  useEffect(() => {
    setContext((prev) => ({
      ...prev,
      sessionId,
      userId: user?.id || prev.userId,
      organizationId: user?.organization_id || prev.organizationId,
      currentPage: pageContext.pathname,
    }));
  }, [sessionId, user?.id, user?.organization_id, pageContext.pathname]);

  useEffect(() => {
    setCurrentSession((prev) => {
      if (!prev) {
        const session: ConversationSession = {
          id: sessionId,
          userId: user?.id || 'anonymous',
          startTime: new Date(),
          lastActivity: new Date(),
          context: {
            ...context,
            sessionId,
          },
          threads: [],
          isActive: true,
        };
        persistContext(session.context, session, sessionId);
        return session;
      }

      const updated: ConversationSession = {
        ...prev,
        id: sessionId,
        userId: user?.id || prev.userId,
        lastActivity: new Date(),
        context: {
          ...context,
          sessionId,
        },
      };
      persistContext(updated.context, updated, sessionId);
      return updated;
    });
  }, [sessionId, user?.id, context, persistContext]);

  const updateWorkflow = useCallback((workflow: WorkflowStatus | null) => {
    setEmmaState((prev) => ({
      ...prev,
      currentWorkflow: workflow,
    }));
  }, []);

  const addStatusUpdate = useCallback((update: Omit<StatusUpdate, 'id' | 'timestamp'>) => {
    const newUpdate: StatusUpdate = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      timestamp: new Date(),
      ...update,
    };

    setEmmaState((prev) => ({
      ...prev,
      statusUpdates: [...prev.statusUpdates, newUpdate].slice(-50),
    }));
  }, []);

  const toggleMinimize = useCallback(() => {
    setEmmaState((prev) => ({
      ...prev,
      isMinimized: !prev.isMinimized,
    }));
  }, []);

  const setVisibility = useCallback((visible: boolean) => {
    setEmmaState((prev) => ({
      ...prev,
      isVisible: visible,
    }));
  }, []);

  const clearHistory = useCallback(() => {
    setEmmaState((prev) => ({
      ...prev,
      statusUpdates: [],
    }));
  }, []);

  const updatePersistentInterface = useCallback((updates: Partial<EmmaState['persistentInterface']>) => {
    setEmmaState((prev) => ({
      ...prev,
      persistentInterface: {
        ...prev.persistentInterface,
        ...updates,
      },
    }));
  }, []);

  const updateContext = useCallback(
    (updates: Partial<EmmaConversationContext>) => {
      setError(null);
      setContext((prev) => {
        const merged: EmmaConversationContext = {
          ...prev,
          ...updates,
          sessionId,
        };
        const updatedSession = currentSession
          ? {
              ...currentSession,
              context: merged,
              lastActivity: new Date(),
            }
          : null;
        persistContext(merged, updatedSession, sessionId);
        if (updatedSession) setCurrentSession(updatedSession);
        return merged;
      });
    },
    [currentSession, persistContext, sessionId],
  );

  const preserveContext = useCallback(() => {
    const nextSession =
      currentSession ||
      ({
        id: sessionId,
        userId: user?.id || 'anonymous',
        startTime: new Date(),
        lastActivity: new Date(),
        context: {
          ...context,
          sessionId,
        },
        threads: [],
        isActive: true,
      } as ConversationSession);
    persistContext(context, nextSession, sessionId);
  }, [context, currentSession, persistContext, sessionId, user?.id]);

  const restoreContext = useCallback(
    async (targetSessionId: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const storedSession = parseStoredJson<ConversationSession>(localStorage.getItem(EMMA_SESSION_STORAGE_KEY));
        const storedContext = parseStoredJson<EmmaConversationContext>(localStorage.getItem(EMMA_CONTEXT_STORAGE_KEY));
        if (storedSession && storedContext && storedSession.id === targetSessionId) {
          const hydratedSession = hydrateSession(storedSession);
          const restoredContext: EmmaConversationContext = {
            ...defaultConversationContext,
            ...storedContext,
            sessionId: targetSessionId,
          };
          setCurrentSession(hydratedSession);
          setContext(restoredContext);
          setSessionId(targetSessionId);
          persistContext(restoredContext, hydratedSession, targetSessionId);
          return;
        }

        const response = await apiRequest(`/api/agents/emma/conversation/${targetSessionId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Failed to restore Emma session (${response.status})`);
        }

        const remoteSession = (await response.json()) as ConversationSession;
        const hydratedRemote = hydrateSession(remoteSession);
        const restoredContext: EmmaConversationContext = {
          ...defaultConversationContext,
          ...hydratedRemote.context,
          sessionId: targetSessionId,
        };
        setCurrentSession(hydratedRemote);
        setContext(restoredContext);
        setSessionId(targetSessionId);
        persistContext(restoredContext, hydratedRemote, targetSessionId);
      } catch (restoreError) {
        setError(restoreError instanceof Error ? restoreError.message : 'Failed to restore Emma context');
        throw restoreError;
      } finally {
        setIsLoading(false);
      }
    },
    [persistContext],
  );

  const clearContext = useCallback(() => {
    try {
      localStorage.removeItem(EMMA_SESSION_STORAGE_KEY);
      localStorage.removeItem(EMMA_CONTEXT_STORAGE_KEY);
      localStorage.removeItem(EMMA_SESSION_ID_STORAGE_KEY);
    } catch (clearError) {
      console.warn('Failed to clear Emma context/session:', clearError);
    }

    const nextSessionId = generateSessionId();
    const nextContext = {
      ...defaultConversationContext,
      sessionId: nextSessionId,
    };
    setSessionId(nextSessionId);
    setContext(nextContext);
    setCurrentSession({
      id: nextSessionId,
      userId: user?.id || 'anonymous',
      startTime: new Date(),
      lastActivity: new Date(),
      context: nextContext,
      threads: [],
      isActive: true,
    });
    setError(null);
  }, [user?.id]);

  const setPageContext = useCallback((updates: Partial<EmmaPageContext>) => {
    setPageContextState((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const contextValue = useMemo<EmmaContextType>(
    () => ({
      emmaState,
      context,
      sessionId,
      pageContext,
      isLoading,
      error,
      updateWorkflow,
      addStatusUpdate,
      toggleMinimize,
      setVisibility,
      clearHistory,
      updatePersistentInterface,
      updateContext,
      preserveContext,
      restoreContext,
      clearContext,
      setPageContext,
    }),
    [
      emmaState,
      context,
      sessionId,
      pageContext,
      isLoading,
      error,
      updateWorkflow,
      addStatusUpdate,
      toggleMinimize,
      setVisibility,
      clearHistory,
      updatePersistentInterface,
      updateContext,
      preserveContext,
      restoreContext,
      clearContext,
      setPageContext,
    ],
  );

  return <EmmaContext.Provider value={contextValue}>{children}</EmmaContext.Provider>;
};

export const useEmmaContext = (): EmmaContextType => {
  const contextValue = useContext(EmmaContext);
  if (!contextValue) {
    throw new Error('useEmmaContext must be used within EmmaContextProvider');
  }
  return contextValue;
};

export default EmmaContext;
